import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Mail, Lock, Building2, Bitcoin } from 'lucide-react';
import { toast } from 'sonner';
import './AuthPage.css';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isAgentSignup, setIsAgentSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    business_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_routing_number: '',
    bank_account_type: 'checking',
    // KYC Fields
    tax_id: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    owner_name: '',
    owner_dob: '',
    estimated_monthly_volume: '',
    business_description: '',
    // Compliance
    terms_accepted: false,
    privacy_accepted: false
  });
  
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let endpoint = '/auth/login';
      
      if (!isLogin) {
        // Choose registration endpoint based on account type
        endpoint = isAgentSignup ? '/auth/register-agent' : '/auth/register';
      }
      
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      login(response.data.access_token, response.data.user);
      
      const userData = response.data.user;
      
      if (isLogin) {
        toast.success('Welcome back!');
      } else if (isAgentSignup) {
        toast.success('Agent account created successfully!');
      } else {
        toast.success('Business account created successfully!');
      }
      
      // Redirect based on user role
      if (userData.is_super_admin && userData.role === 'super_admin') {
        navigate('/super-admin');
      } else if (userData.is_agent || userData.role === 'agent') {
        navigate('/agent');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="auth-page" data-testid="auth-page">
      <div className="auth-container">
        <div className="auth-card" data-testid="auth-card">
          <div className="auth-header" data-testid="auth-header">
            <div className="auth-logo" data-testid="auth-logo">
              <Bitcoin size={40} />
            </div>
            <h1 className="auth-title" data-testid="auth-title">
              {isLogin ? 'Welcome Back' : (isAgentSignup ? 'Become a Partner' : 'Sign Up as Business')}
            </h1>
            <p className="auth-subtitle" data-testid="auth-subtitle">
              {isLogin 
                ? 'Sign in to manage your crypto payments' 
                : (isAgentSignup 
                  ? 'Partner with us and earn revenue from crypto payments'
                  : 'Start accepting crypto payments for your business')}
            </p>
          </div>

          {/* Account Type Tabs (only show during registration) */}
          {!isLogin && (
            <div className="account-type-tabs">
              <button
                type="button"
                className={`account-tab ${!isAgentSignup ? 'active' : ''}`}
                onClick={() => setIsAgentSignup(false)}
              >
                <Building2 size={18} />
                Business Account
              </button>
              <button
                type="button"
                className={`account-tab ${isAgentSignup ? 'active' : ''}`}
                onClick={() => setIsAgentSignup(true)}
              >
                <Building2 size={18} />
                Partner/Agent Account
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" data-testid="auth-form">
            {!isLogin && (
              <>
                <div className="form-group">
                  <label data-testid="business-name-label">
                    <Building2 size={18} /> Business Name
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    placeholder="Enter your business name"
                    required={!isLogin}
                    data-testid="business-name-input"
                  />
                </div>

                {/* Bank Account Information Section - Only for Business accounts, not Agents */}
                {!isAgentSignup && (
                <div className="bank-info-section">
                  <h3 className="section-title">Bank Account Information</h3>
                  <p className="section-note">For $15 monthly subscription + same-day payouts (add $0.50 per transaction)</p>
                  <p className="section-note">📄 We'll verify with a voided check or direct deposit form after signup</p>
                  
                  <div className="form-group">
                    <label data-testid="bank-account-name-label">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      name="bank_account_name"
                      value={formData.bank_account_name}
                      onChange={handleChange}
                      placeholder="Full name on bank account"
                      required={!isLogin}
                      data-testid="bank-account-name-input"
                    />
                  </div>

                  <div className="form-group">
                    <label data-testid="bank-routing-label">
                      Routing Number
                    </label>
                    <input
                      type="text"
                      name="bank_routing_number"
                      value={formData.bank_routing_number}
                      onChange={handleChange}
                      placeholder="9-digit routing number"
                      maxLength="9"
                      pattern="[0-9]{9}"
                      required={!isLogin}
                      data-testid="bank-routing-input"
                    />
                  </div>

                  <div className="form-group">
                    <label data-testid="bank-account-number-label">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bank_account_number"
                      value={formData.bank_account_number}
                      onChange={handleChange}
                      placeholder="Bank account number"
                      required={!isLogin}
                      data-testid="bank-account-number-input"
                    />
                  </div>

                  <div className="form-group">
                    <label data-testid="bank-account-type-label">
                      Account Type
                    </label>
                    <select
                      name="bank_account_type"
                      value={formData.bank_account_type}
                      onChange={handleChange}
                      required={!isLogin}
                      data-testid="bank-account-type-select"
                    >
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                </div>
                )}

                {/* KYC Compliance Section - Only for Business accounts, not Agents */}
                {!isAgentSignup && (
                <div className="kyc-info-section">
                  <h3 className="section-title">Business Verification (KYC)</h3>
                  <p className="section-note">Required for compliance and account verification</p>
                  
                  <div className="form-group">
                    <label>Tax ID (EIN or SSN)</label>
                    <input
                      type="text"
                      name="tax_id"
                      value={formData.tax_id}
                      onChange={handleChange}
                      placeholder="XX-XXXXXXX or XXX-XX-XXXX"
                      required={!isLogin}
                    />
                  </div>

                  <div className="form-group">
                    <label>Business Address</label>
                    <input
                      type="text"
                      name="business_address"
                      value={formData.business_address}
                      onChange={handleChange}
                      placeholder="Street address"
                      required={!isLogin}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="business_city"
                        value={formData.business_city}
                        onChange={handleChange}
                        placeholder="City"
                        required={!isLogin}
                      />
                    </div>

                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="business_state"
                        value={formData.business_state}
                        onChange={handleChange}
                        placeholder="State"
                        maxLength="2"
                        required={!isLogin}
                      />
                    </div>

                    <div className="form-group">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        name="business_zip"
                        value={formData.business_zip}
                        onChange={handleChange}
                        placeholder="ZIP"
                        pattern="[0-9]{5}"
                        maxLength="5"
                        required={!isLogin}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Owner/Principal Name</label>
                    <input
                      type="text"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      placeholder="Full legal name"
                      required={!isLogin}
                    />
                  </div>

                  <div className="form-group">
                    <label>Owner Date of Birth</label>
                    <input
                      type="date"
                      name="owner_dob"
                      value={formData.owner_dob}
                      onChange={handleChange}
                      required={!isLogin}
                    />
                  </div>

                  <div className="form-group">
                    <label>Estimated Monthly Transaction Volume</label>
                    <select
                      name="estimated_monthly_volume"
                      value={formData.estimated_monthly_volume}
                      onChange={handleChange}
                      required={!isLogin}
                    >
                      <option value="">Select range...</option>
                      <option value="0-5000">$0 - $5,000</option>
                      <option value="5000-25000">$5,000 - $25,000</option>
                      <option value="25000-100000">$25,000 - $100,000</option>
                      <option value="100000+">$100,000+</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Business Description</label>
                    <textarea
                      name="business_description"
                      value={formData.business_description}
                      onChange={handleChange}
                      placeholder="Brief description of your business and what you sell"
                      rows="3"
                      required={!isLogin && !isAgentSignup}
                    />
                  </div>
                </div>
                )}

                {/* Terms of Service Acceptance - Only for Business accounts, not Agents */}
                {!isAgentSignup && (
                <div className="terms-section">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="terms_accepted"
                      name="terms_accepted"
                      checked={formData.terms_accepted}
                      onChange={(e) => setFormData({...formData, terms_accepted: e.target.checked})}
                      required={!isLogin && !isAgentSignup}
                    />
                    <label htmlFor="terms_accepted">
                      I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    </label>
                  </div>

                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="privacy_accepted"
                      name="privacy_accepted"
                      checked={formData.privacy_accepted}
                      onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})}
                      required={!isLogin && !isAgentSignup}
                    />
                    <label htmlFor="privacy_accepted">
                      I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    </label>
                  </div>

                  <div className="info-note">
                    <p><strong>By registering, you acknowledge:</strong></p>
                    <ul>
                      <li>Crypto Cashier acts as a Payment Facilitator</li>
                      <li>Funds are temporarily held for processing (24-48 hours)</li>
                      <li>Platform fees are automatically deducted</li>
                      <li>You authorize ACH debits for subscription fees</li>
                    </ul>
                  </div>
                </div>
                )}
              </>
            )}

            <div className="form-group">
              <label data-testid="email-label">
                <Mail size={18} /> Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label data-testid="password-label">
                <Lock size={18} /> Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                data-testid="password-input"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-full" 
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="auth-toggle" data-testid="auth-toggle">
            <p>
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button 
                onClick={() => setIsLogin(!isLogin)} 
                className="toggle-btn"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;